// @flow

import React from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";
import { GSGrid, Pagination } from "@components";
import type { State } from "@store/reducers";
import type { PaginationExtendedType } from "@types";
import actionCreators from "@modules/shared/actionCreator";
import { EMPLOYEE_REF_TYPES } from "@modules/shared/constants/notifications";
import type {
  EmployeeRefType,
  HierarchyNotification,
} from "@modules/shared/reducer/employee-notifications/types";
import {
  getNotificationsDepartments,
  getNotificationsDepartmentsPagination,
} from "@modules/shared/selectors/employee-notifications";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  employeeRefType: EmployeeRefType,
}>;

type PropsFromState = $Exact<{
  notifications: Array<HierarchyNotification>,
  pagination: PaginationExtendedType,
}>;

type PropsFromDispatch = $Exact<{
  getHierarchyNotifications: typeof actionCreators.GetHierarchyNotifications,
  getHierarchyNotificationsTargets: typeof actionCreators.GetHierarchyNotificationsTargets,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class NotificationsDepartments extends React.Component<Props> {
  componentDidMount() {
    this.getHierarchyNotifications();
  }

  componentDidUpdate(prevProps: Props) {
    if (
      this.props.employeeRefType &&
      prevProps.employeeRefType &&
      this.props.employeeRefType !== prevProps.employeeRefType
    ) {
      this.getHierarchyNotifications();
    }
  }

  getHierarchyNotifications = () => {
    if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT) {
      this.props.getHierarchyNotifications({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.getHierarchyNotificationsTargets({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    }
  };

  handlePaginationChange = (page: number) => {
    if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT) {
      this.props.getHierarchyNotifications({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        page,
      });
    } else if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.TARGET) {
      this.props.getHierarchyNotificationsTargets({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
        page,
      });
    }
  };

  get tableColumns() {
    return {
      [EMPLOYEE_REF_TYPES.RECIPIENT]: [
        {
          key: "employeeRef",
          name: this.props.t("NOTIFICATIONS_EMPLOYEE_REF"),
          renderCell: ({ row }: { row: HierarchyNotification }) => row.recipient.employeeRefPrimary,
        },
        {
          key: "employeeName",
          name: this.props.t("NOTIFICATIONS_EMPLOYEE_NAME"),
          renderCell: ({ row }: { row: HierarchyNotification }) =>
            `${row.recipient.firstName} ${row.recipient.lastName}`,
          align: "left",
        },
        {
          key: "departmentRef",
          name: this.props.t("NOTIFICATIONS_DEPARTMENT_REF"),
          renderCell: ({ row }: { row: HierarchyNotification }) =>
            row.isInherited
              ? row.inheritedFrom.organisationalUnitReference
              : row.employeePosition.organisationalUnit.organisationalUnitReference,
          align: "left",
        },
        {
          key: "departmentName",
          name: this.props.t("NOTIFICATIONS_DEPARTMENT_NAME"),
          renderCell: ({ row }: { row: HierarchyNotification }) =>
            row.isInherited ? row.inheritedFrom.name : row.employeePosition.organisationalUnit.name,
          align: "left",
        },
        {
          key: "notificationType",
          name: this.props.t("NOTIFICATIONS_NOTIFICATION_TYPE"),
          renderCell: ({ row }: { row: HierarchyNotification }) =>
            this.props.t(row.notificationType),
          align: "left",
        },
      ],
      [EMPLOYEE_REF_TYPES.TARGET]: [
        {
          key: "departmentRef",
          name: this.props.t("NOTIFICATIONS_DEPARTMENT_REF"),
          renderCell: ({ row }: { row: HierarchyNotification }) =>
            row.organisationalUnit.organisationalUnitReference,
          align: "left",
        },
        {
          key: "departmentName",
          name: this.props.t("NOTIFICATIONS_DEPARTMENT_NAME"),
          renderCell: ({ row }: { row: HierarchyNotification }) => row.organisationalUnit.name,
          align: "left",
        },
        {
          key: "notificationType",
          name: this.props.t("NOTIFICATIONS_NOTIFICATION_TYPE"),
          renderCell: ({ row }: { row: HierarchyNotification }) =>
            this.props.t(row.notificationType),
          align: "left",
        },
        {
          key: "notificationScope",
          name: this.props.t("NOTIFICATION_SCOPE"),
          renderCell: ({ row }: { row: HierarchyNotification }) =>
            row.isCascading
              ? this.props.t("DEPARTMENT_AND_CHILDREN")
              : this.props.t("DEPARTMENT_ONLY"),
          align: "left",
        },
      ],
    };
  }

  render() {
    return (
      <React.Fragment>
        <GSGrid
          rows={this.props.notifications}
          columns={this.tableColumns[this.props.employeeRefType]}
        />
        {this.props.pagination ? (
          <Pagination
            className="mx-auto mt-3 d-block"
            currentPage={this.props.pagination.pageNumber}
            totalPages={this.props.pagination.totalPages}
            onChange={this.handlePaginationChange}
          />
        ) : null}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: State, ownProps: OwnProps): PropsFromState => ({
  notifications: getNotificationsDepartments(state, ownProps.employeeRefType),
  pagination: getNotificationsDepartmentsPagination(state, ownProps.employeeRefType),
});

const mapDispatchToProps: PropsFromDispatch = {
  getHierarchyNotifications: actionCreators.GetHierarchyNotifications,
  getHierarchyNotificationsTargets: actionCreators.GetHierarchyNotificationsTargets,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(NotificationsDepartments);
